import { Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faPlus,
  faTrashCan,
} from "@fortawesome/pro-solid-svg-icons";
import {
  FACT_OPTIONS,
  MAX_RULE_PERCENT,
  OPS_BY_FACT,
  OP_LABELS,
  PRICING_MODEL_OPTIONS,
  RateRule,
  RateRuleCondition,
  RateRuleFact,
  RateRuleOp,
  fractionToPercent,
  newCondition,
  newRule,
  percentToFraction,
  withFact,
} from "./rateRules";
import "./style.css";

interface Props {
  rules: RateRule[];
  onChange: (rules: RateRule[]) => void;
}

// Controlled: every input reads from `rules`, so index keys stay correct
// across reorders.
export const RateRulesEditor = ({ rules, onChange }: Props) => {
  const replaceRule = (index: number, rule: RateRule) =>
    onChange(rules.map((r, i) => (i === index ? rule : r)));

  const replaceCondition = (
    ruleIndex: number,
    conditionIndex: number,
    condition: RateRuleCondition
  ) => {
    const rule = rules[ruleIndex];
    replaceRule(ruleIndex, {
      ...rule,
      conditions: rule.conditions.map((c, i) =>
        i === conditionIndex ? condition : c
      ),
    });
  };

  const moveRule = (from: number, to: number) => {
    const next = [...rules];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className="rateRules">
      {rules.length === 0 && (
        <div className="rateRulesEmpty">
          No rules. The base percentage always applies.
        </div>
      )}
      {rules.map((rule, ruleIndex) => {
        const ruleNumber = ruleIndex + 1;
        const percent = fractionToPercent(rule.rate);
        return (
          <div
            className="rateRule"
            role="group"
            aria-label={`Rule ${ruleNumber}`}
            key={ruleIndex}
          >
            <div className="rateRuleHeader">
              <span className="rateRuleTitle">Rule {ruleNumber}</span>
              <span className="rateRuleActions">
                <Button
                  variant="link"
                  size="sm"
                  aria-label={`Move rule ${ruleNumber} up`}
                  disabled={ruleIndex === 0}
                  onClick={() => moveRule(ruleIndex, ruleIndex - 1)}
                >
                  <FontAwesomeIcon icon={faArrowUp} />
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  aria-label={`Move rule ${ruleNumber} down`}
                  disabled={ruleIndex === rules.length - 1}
                  onClick={() => moveRule(ruleIndex, ruleIndex + 1)}
                >
                  <FontAwesomeIcon icon={faArrowDown} />
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  aria-label={`Remove rule ${ruleNumber}`}
                  onClick={() =>
                    onChange(rules.filter((_, i) => i !== ruleIndex))
                  }
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </Button>
              </span>
            </div>

            {rule.conditions.map((condition, conditionIndex) => {
              const name = `Rule ${ruleNumber} condition ${conditionIndex + 1}`;
              const update = (changes: Partial<RateRuleCondition>) =>
                replaceCondition(ruleIndex, conditionIndex, {
                  ...condition,
                  ...changes,
                });
              return (
                <div className="rateRuleRow" key={conditionIndex}>
                  <span className="rateRuleWord">
                    {conditionIndex === 0 ? "If" : "and"}
                  </span>
                  <Form.Control
                    as="select"
                    aria-label={`${name} fact`}
                    value={condition.fact}
                    onChange={(e) =>
                      replaceCondition(
                        ruleIndex,
                        conditionIndex,
                        withFact(condition, e.target.value as RateRuleFact)
                      )
                    }
                  >
                    {FACT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Control
                    as="select"
                    aria-label={`${name} operator`}
                    value={condition.op}
                    onChange={(e) =>
                      update({ op: e.target.value as RateRuleOp })
                    }
                  >
                    {OPS_BY_FACT[condition.fact].map((op) => (
                      <option key={op} value={op}>
                        {OP_LABELS[op]}
                      </option>
                    ))}
                  </Form.Control>
                  {condition.fact === "effort" ? (
                    <Form.Control
                      type="number"
                      aria-label={`${name} hours`}
                      placeholder="Hours"
                      min={0}
                      step={0.01}
                      required
                      value={condition.value ?? ""}
                      onChange={(e) =>
                        update({
                          value:
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                        })
                      }
                    />
                  ) : (
                    <Form.Control
                      as="select"
                      aria-label={`${name} pricing model`}
                      required
                      value={condition.value ?? ""}
                      onChange={(e) =>
                        update({ value: e.target.value || null })
                      }
                    >
                      <option value="">Select…</option>
                      {PRICING_MODEL_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Form.Control>
                  )}
                  <Button
                    variant="link"
                    size="sm"
                    aria-label={`Remove rule ${ruleNumber} condition ${
                      conditionIndex + 1
                    }`}
                    disabled={rule.conditions.length === 1}
                    onClick={() =>
                      replaceRule(ruleIndex, {
                        ...rule,
                        conditions: rule.conditions.filter(
                          (_, i) => i !== conditionIndex
                        ),
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faTrashCan} />
                  </Button>
                </div>
              );
            })}

            <Button
              variant="link"
              size="sm"
              className="rateRuleAdd"
              onClick={() =>
                replaceRule(ruleIndex, {
                  ...rule,
                  conditions: [...rule.conditions, newCondition()],
                })
              }
            >
              <FontAwesomeIcon icon={faPlus} /> Add condition
            </Button>

            <div className="rateRuleRow">
              <span className="rateRuleWord">then use</span>
              <Form.Control
                type="number"
                className="rateRulePercent"
                aria-label={`Rule ${ruleNumber} percentage`}
                placeholder="%"
                min={0}
                max={MAX_RULE_PERCENT}
                step={0.01}
                required
                value={percent ?? ""}
                onChange={(e) =>
                  replaceRule(ruleIndex, {
                    ...rule,
                    rate: percentToFraction(e.target.value),
                  })
                }
              />
              <span className="rateRuleWord">% of hours</span>
            </div>
          </div>
        );
      })}
      <Button
        variant="link"
        className="rateRuleAdd"
        onClick={() => onChange([...rules, newRule()])}
      >
        <FontAwesomeIcon icon={faPlus} /> Add rule
      </Button>
    </div>
  );
};
